/**
 * Rate Limiter Utility
 * Prevents spam by limiting how frequently actions can be performed
 */

interface RateLimitEntry {
  count: number;
  firstAttempt: number;
  lastAttempt: number;
}

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number; // Time window in milliseconds
  blockDurationMs?: number; // How long to block after exceeding limit
}

class RateLimiter {
  private storage: Map<string, RateLimitEntry> = new Map();
  private cleanupInterval: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Clean up old entries every 5 minutes
    this.cleanupInterval = setInterval(() => this.cleanup(), 5 * 60 * 1000);
  }

  /**
   * Check if an action is allowed based on rate limits
   * @param key - Unique identifier (e.g., userId, IP, action type)
   * @param config - Rate limit configuration
   * @returns Object with allowed status and remaining attempts
   */
  check(key: string, config: RateLimitConfig): { 
    allowed: boolean; 
    remaining: number;
    resetAt: number;
    blockedUntil?: number;
  } {
    const now = Date.now();
    const entry = this.storage.get(key);

    // No previous attempts
    if (!entry) {
      this.storage.set(key, {
        count: 1,
        firstAttempt: now,
        lastAttempt: now
      });
      return { 
        allowed: true, 
        remaining: config.maxAttempts - 1,
        resetAt: now + config.windowMs
      };
    }

    // Check if window has expired
    if (now - entry.firstAttempt > config.windowMs) {
      // Reset the window
      this.storage.set(key, {
        count: 1,
        firstAttempt: now,
        lastAttempt: now
      });
      return { 
        allowed: true, 
        remaining: config.maxAttempts - 1,
        resetAt: now + config.windowMs
      };
    }

    // Check if blocked
    if (config.blockDurationMs && entry.count >= config.maxAttempts) {
      const blockUntil = entry.lastAttempt + config.blockDurationMs;
      if (now < blockUntil) {
        return { 
          allowed: false, 
          remaining: 0,
          resetAt: entry.firstAttempt + config.windowMs,
          blockedUntil: blockUntil
        };
      }
      // Block expired, reset
      this.storage.set(key, {
        count: 1,
        firstAttempt: now,
        lastAttempt: now
      });
      return { 
        allowed: true, 
        remaining: config.maxAttempts - 1,
        resetAt: now + config.windowMs
      };
    }

    // Within window, check limit
    if (entry.count >= config.maxAttempts) {
      return { 
        allowed: false, 
        remaining: 0,
        resetAt: entry.firstAttempt + config.windowMs
      };
    }

    // Increment count
    entry.count++;
    entry.lastAttempt = now;
    this.storage.set(key, entry);

    return { 
      allowed: true, 
      remaining: config.maxAttempts - entry.count,
      resetAt: entry.firstAttempt + config.windowMs
    };
  }

  /**
   * Reset rate limit for a specific key
   */
  reset(key: string): void {
    this.storage.delete(key);
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    const maxAge = 60 * 60 * 1000; // 1 hour

    for (const [key, entry] of this.storage.entries()) {
      if (now - entry.lastAttempt > maxAge) {
        this.storage.delete(key);
      }
    }
  }

  /**
   * Destroy the rate limiter
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.storage.clear();
  }
}

// Singleton instance
const rateLimiter = new RateLimiter();

// Predefined rate limit configurations
export const RateLimitConfigs = {
  // Order/Booking limits
  ORDER_PLACEMENT: {
    maxAttempts: 3,
    windowMs: 60 * 1000, // 1 minute
    blockDurationMs: 5 * 60 * 1000 // 5 minutes
  },
  
  // Form submissions
  CONTACT_FORM: {
    maxAttempts: 3,
    windowMs: 5 * 60 * 1000, // 5 minutes
    blockDurationMs: 15 * 60 * 1000 // 15 minutes
  },
  
  FEEDBACK_FORM: {
    maxAttempts: 5,
    windowMs: 10 * 60 * 1000, // 10 minutes
    blockDurationMs: 30 * 60 * 1000 // 30 minutes
  },
  
  // Authentication
  LOGIN_ATTEMPTS: {
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
    blockDurationMs: 30 * 60 * 1000 // 30 minutes
  },
  
  SIGNUP_ATTEMPTS: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    blockDurationMs: 2 * 60 * 60 * 1000 // 2 hours
  },
  
  // Promo code validation
  PROMO_CODE_CHECK: {
    maxAttempts: 10,
    windowMs: 5 * 60 * 1000, // 5 minutes
  },
  
  // API calls
  API_CALL_GENERAL: {
    maxAttempts: 100,
    windowMs: 60 * 1000, // 1 minute
  },
  
  // Cart operations
  CART_UPDATE: {
    maxAttempts: 50,
    windowMs: 60 * 1000, // 1 minute
  }
};

/**
 * Check if an action is rate limited
 * @param userId - User identifier (or IP/session if not logged in)
 * @param action - Action type
 * @param config - Optional custom rate limit config
 */
export function checkRateLimit(
  userId: string,
  action: keyof typeof RateLimitConfigs,
  config?: RateLimitConfig
): { 
  allowed: boolean; 
  remaining: number;
  resetAt: number;
  blockedUntil?: number;
  message?: string;
} {
  const key = `${userId}:${action}`;
  const limitConfig = config || RateLimitConfigs[action];
  const baseResult = rateLimiter.check(key, limitConfig);

  // Add user-friendly messages
  const result: {
    allowed: boolean;
    remaining: number;
    resetAt: number;
    blockedUntil?: number;
    message?: string;
  } = { ...baseResult };

  if (!result.allowed) {
    const minutesUntilReset = Math.ceil((result.resetAt - Date.now()) / 60000);
    if (result.blockedUntil) {
      const minutesUntilUnblock = Math.ceil((result.blockedUntil - Date.now()) / 60000);
      result.message = `Too many attempts. Please try again in ${minutesUntilUnblock} minute${minutesUntilUnblock !== 1 ? 's' : ''}.`;
    } else {
      result.message = `Rate limit exceeded. Please try again in ${minutesUntilReset} minute${minutesUntilReset !== 1 ? 's' : ''}.`;
    }
  }

  return result;
}

/**
 * Reset rate limit for a user action
 */
export function resetRateLimit(userId: string, action: keyof typeof RateLimitConfigs): void {
  const key = `${userId}:${action}`;
  rateLimiter.reset(key);
}

/**
 * Get a browser/device fingerprint for anonymous users
 */
export function getDeviceFingerprint(): string {
  // Combine various browser properties to create a semi-unique identifier
  const properties = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 0,
    (navigator as any).deviceMemory || 0
  ];
  
  // Simple hash function
  const hash = properties.join('|').split('').reduce((acc, char) => {
    return ((acc << 5) - acc) + char.charCodeAt(0);
  }, 0);
  
  return `device_${Math.abs(hash)}`;
}

export default rateLimiter;
